// Copyright (c) 2026 Michael F. Kamprath
//
// home.js -- home screen with Learn, Quiz, and Settings buttons.
//
// Learn + Quiz behave as before: the selected button enlarges, the other
// shrinks and dims, the mode-specific session-start prompt plays, and
// the onLearn / onQuiz handler fires.
//
// The Settings button -- new per docs/SETTINGS-SPEC.md §2 -- is an
// inline labeled button that sits BELOW Learn/Quiz at rest.  It is
// gated by a three-tap state machine:
//
//   state 0 (neutral, muted slate)   ─ tap ─▶  state 1 (primed, amber)
//   state 1 (primed, amber)          ─ tap ─▶  state 2 (ready, green)
//                                              + slides ABOVE Learn/Quiz
//   state 2 (ready, green)           ─ tap ─▶  onSettings() (caller opens
//                                              panel), gate resets
//
// Reset conditions (state → 0):
//   - 4 s idle from state 1 or 2
//   - any tap outside the Settings button while the gate is non-zero
//
// Tap-to-close: if the panel is already open, a tap on Settings bypasses
// the gate entirely and calls onSettings() (caller toggles / closes the
// panel).  Programmatic open via the panel handle also bypasses the
// gate -- home.js doesn't try to track that; the caller manages it.

import { createHomeMascot } from './home-mascot.js';
import { trackUiAction } from '../telemetry.js';
import { t } from './strings/index.js';

const SELECT_ANIMATION_MS_DEFAULT = 600;

/** Idle timeout before a partially-advanced gate resets to neutral (spec §2). */
const GATE_IDLE_RESET_MS = 4000;

/** Duration of the "slide above Learn/Quiz" reorder transition (spec §2). */
const GATE_REORDER_MS = 250;

const noop = () => {};

/**
 * Create the home screen and attach it to the parent.
 *
 * @param {HTMLElement} parent
 * @param {{
 *   onLearn?: () => void,
 *   onQuiz?: () => void,
 *   playSelectPrompt?: (mode: 'learn'|'quiz') => Promise<void>,
 *   selectAnimationMs?: number,
 *   onSettings?: () => void,
 *     Called when the three-tap gate clears on Settings, OR when the
 *     Settings button is tapped while the panel is already open (tap-
 *     to-close).  Caller decides whether to open or close the panel.
 *   isSettingsOpen?: () => boolean,
 *     Optional probe for "is the settings panel currently open".  When
 *     it returns true, the Settings button bypasses the gate and fires
 *     onSettings directly (tap-to-close semantics, spec §2 dismissal).
 *   playSheepBaa?: (src: string) => Promise<void>,
 *     Optional audio callback for the jump+baa tap reaction on the
 *     home-screen mascot.  Wired by app.js to the project's shared
 *     one-shot playback helper; the mascot defaults to a no-op when
 *     omitted (e.g., in tests that don't exercise audio).
 *   mascotConfig?: object,
 *     Optional tunables for the mascot (graze / blink cadence, reaction
 *     mix, size, position, jump peak Y).  Shape matches the return of
 *     getHomeMascotConfig() in src/app-config.js.  app.js builds this
 *     from public/app-config.json; tests typically skip it.
 * }} [opts]
 * @returns {{
 *   element: HTMLElement,
 *   hide: () => void,
 *   show: () => void,
 *   setSelectAnimationMs: (ms: number) => void,
 *   __getGateState: () => number,  // test-only accessor
 *   __resetGate: () => void,       // test-only accessor
 * }}
 */
export function createHomeScreen(parent, opts = {}) {
  const onLearn = opts.onLearn ?? noop;
  const onQuiz = opts.onQuiz ?? noop;
  const onSettings = opts.onSettings ?? noop;
  const isSettingsOpen = typeof opts.isSettingsOpen === 'function'
    ? opts.isSettingsOpen
    : () => false;
  const playSelectPrompt = typeof opts.playSelectPrompt === 'function'
    ? opts.playSelectPrompt
    : null;
  const selectAnimationMs = Number.isFinite(opts.selectAnimationMs)
    ? opts.selectAnimationMs
    : SELECT_ANIMATION_MS_DEFAULT;
  // UI string locale -- defaults to 'en' so existing tests + callers
  // that don't pass it continue to render English (same behavior as
  // before slice 3d).  app.js passes getLocale() and rebuilds the home
  // screen on locale change so the buttons re-localize.
  const locale = typeof opts.locale === 'string' && opts.locale ? opts.locale : 'en';

  const el = document.createElement('div');
  el.className = 'home-screen';

  // Title
  const title = document.createElement('h1');
  title.className = 'home-screen__title';
  title.textContent = t('home.title', locale);
  el.appendChild(title);

  // Animated mascot sheep: sits between title and nav, runs its own idle
  // grazing cycle, and reacts to tap with a 50/50 mix of a silent rock
  // and a jump+baa.  Not inside .home-screen__nav on purpose -- the
  // three-tap settings gate uses z-axis reorder on nav's children, and
  // the mascot is a peer, not a participant.
  const mascot = createHomeMascot({
    playSheepBaa: typeof opts.playSheepBaa === 'function'
      ? opts.playSheepBaa
      : undefined,
    config: (opts.mascotConfig && typeof opts.mascotConfig === 'object')
      ? opts.mascotConfig
      : undefined,
  });
  el.appendChild(mascot.element);

  // --- Nav container holds BOTH the buttons row AND the settings row.
  // This is the element the gate-ready class applies to; its two
  // children translate on the z-axis when the gate reaches state 2.
  const nav = document.createElement('div');
  nav.className = 'home-screen__nav';
  // Gate-reorder duration lives on nav (applies to its children).
  nav.style.setProperty('--home-gate-reorder-ms', `${GATE_REORDER_MS}ms`);
  el.appendChild(nav);

  // Buttons row (Learn + Quiz).  --home-select-anim-ms stays on this
  // element (not on nav) because the pre-existing test suite queries
  // `.home-screen__buttons` for the custom property -- CSS inheritance
  // would make nav equivalent at runtime, but the style attribute
  // assertion is location-specific.
  const buttons = document.createElement('div');
  buttons.className = 'home-screen__buttons';
  buttons.style.setProperty('--home-select-anim-ms', `${selectAnimationMs}ms`);

  const learnBtn = document.createElement('button');
  learnBtn.type = 'button';
  learnBtn.className = 'home-screen__button home-screen__button--learn';
  learnBtn.textContent = t('home.button.learn', locale);
  buttons.appendChild(learnBtn);

  const quizBtn = document.createElement('button');
  quizBtn.type = 'button';
  quizBtn.className = 'home-screen__button home-screen__button--quiz';
  quizBtn.textContent = t('home.button.quiz', locale);
  buttons.appendChild(quizBtn);

  nav.appendChild(buttons);

  // Settings row (below at rest; slides above when gate reaches ready).
  const settingsWrap = document.createElement('div');
  settingsWrap.className = 'home-screen__settings-wrap';
  const settingsBtn = document.createElement('button');
  settingsBtn.type = 'button';
  settingsBtn.className = 'home-screen__settings';
  settingsBtn.textContent = t('home.button.settings', locale);
  settingsBtn.setAttribute('aria-label', t('home.button.settings', locale));
  settingsBtn.setAttribute('aria-haspopup', 'dialog');
  settingsBtn.setAttribute('aria-expanded', 'false');
  settingsWrap.appendChild(settingsBtn);
  nav.appendChild(settingsWrap);

  parent.appendChild(el);

  // --- Learn/Quiz select state --------------------------------------------
  // (Unchanged from the pre-spec version -- the gate logic lives alongside,
  // not inside, this flow.)
  let selecting = false;

  function resetSelectState() {
    selecting = false;
    buttons.classList.remove('home-screen__buttons--selecting');
    learnBtn.classList.remove('home-screen__button--selected');
    learnBtn.classList.remove('home-screen__button--unselected');
    quizBtn.classList.remove('home-screen__button--selected');
    quizBtn.classList.remove('home-screen__button--unselected');
  }

  async function handleSelect(mode) {
    if (selecting) return;
    selecting = true;

    // Telemetry: one event per home-button tap.  Fire BEFORE the gate
    // reset + select animation so the event goes out even if the
    // animation or scene-start path later throws.
    try {
      trackUiAction({
        action: mode === 'learn' ? 'home_learn_tap' : 'home_quiz_tap',
      });
    } catch { /* non-fatal */ }

    // Any mode-button tap also resets a half-advanced gate.  A child
    // fiddling with Learn between Settings taps shouldn't leave the
    // gate primed.
    resetGate();

    const selectedBtn = mode === 'learn' ? learnBtn : quizBtn;
    const otherBtn = mode === 'learn' ? quizBtn : learnBtn;

    buttons.classList.add('home-screen__buttons--selecting');

    const applyTargetClasses = () => {
      if (!selecting) return;
      selectedBtn.classList.add('home-screen__button--selected');
      otherBtn.classList.add('home-screen__button--unselected');
    };
    if (typeof requestAnimationFrame === 'function') {
      requestAnimationFrame(applyTargetClasses);
    } else {
      setTimeout(applyTargetClasses, 0);
    }

    if (playSelectPrompt) {
      try {
        await playSelectPrompt(mode);
      } catch (err) {
        if (typeof console !== 'undefined' && console.warn) {
          // eslint-disable-next-line no-console
          console.warn('[home] playSelectPrompt failed:', err);
        }
      }
    }

    if (mode === 'learn') onLearn();
    else onQuiz();
  }

  learnBtn.addEventListener('click', () => { handleSelect('learn'); });
  quizBtn.addEventListener('click', () => { handleSelect('quiz'); });

  // --- Settings gate (three-tap state machine) ----------------------------
  //
  // SETTINGS-SPEC §2: a toddler tapping once and walking away should not
  // leave the gate primed long enough for an adult to click through later;
  // similarly, a tap on Learn/Quiz between Settings taps should snap the
  // gate back to neutral.  Both are handled -- the idle timer (4 s) and
  // the outside-tap handler below.  A committed adult can do three taps
  // inside 4 s on the Settings button only; the relocation after tap #2
  // makes tap #3 require deliberate re-aim.
  let gateState = 0;
  let gateTimer = null;

  function clearGateClasses() {
    settingsBtn.classList.remove(
      'home-screen__settings--primed',
      'home-screen__settings--ready',
    );
    nav.classList.remove('home-screen--settings-ready');
  }

  function resetGate() {
    if (gateTimer !== null) {
      clearTimeout(gateTimer);
      gateTimer = null;
    }
    gateState = 0;
    clearGateClasses();
  }

  function scheduleGateReset() {
    if (gateTimer !== null) clearTimeout(gateTimer);
    gateTimer = setTimeout(() => {
      gateTimer = null;
      resetGate();
    }, GATE_IDLE_RESET_MS);
  }

  function enterReadyState() {
    // Measure heights NOW and stash them as CSS custom properties so the
    // transform translates the two rows past each other exactly.  Heights
    // are stable once the layout has settled (the home screen has been
    // visible since show()), so one-shot measurement is reliable.
    //
    // gap is read from getComputedStyle so changing the CSS gap value
    // doesn't desynchronise the reorder.  Falls back to 0 in test
    // environments where getComputedStyle returns an empty string.
    const buttonsH = buttons.offsetHeight || 0;
    const settingsH = settingsWrap.offsetHeight || 0;
    let gap = 0;
    try {
      const cs = typeof getComputedStyle === 'function' ? getComputedStyle(nav) : null;
      const parsed = cs ? parseFloat(cs.rowGap || cs.gap || '0') : 0;
      gap = Number.isFinite(parsed) ? parsed : 0;
    } catch { /* no-op */ }
    nav.style.setProperty('--home-settings-reorder-offset', `${buttonsH + gap}px`);
    nav.style.setProperty('--home-buttons-reorder-offset', `${settingsH + gap}px`);
    nav.classList.add('home-screen--settings-ready');
  }

  function advanceGate() {
    gateState += 1;
    if (gateState === 1) {
      clearGateClasses();
      settingsBtn.classList.add('home-screen__settings--primed');
      scheduleGateReset();
      // Telemetry: settings_gate_start fires on the FIRST tap of
      // the 3-tap gate, never on subsequent taps.  The difference
      // between this count and settings_gate_open tells us how often
      // parents abandon the gate partway through.
      try { trackUiAction({ action: 'settings_gate_start' }); }
      catch { /* non-fatal */ }
    } else if (gateState === 2) {
      clearGateClasses();
      settingsBtn.classList.add('home-screen__settings--ready');
      enterReadyState();
      scheduleGateReset();
    } else {
      // Third tap -- gate cleared.  Reset BEFORE firing onSettings so
      // the button is back in its home position the moment the panel
      // opens, matching the IHE pattern (SETTINGS-SPEC §14).
      resetGate();
      // Telemetry: settings_gate_open only fires on successful
      // completion; abandonment shows up as missing opens relative
      // to starts.
      try { trackUiAction({ action: 'settings_gate_open' }); }
      catch { /* non-fatal */ }
      try { onSettings(); }
      catch (err) {
        if (typeof console !== 'undefined' && console.warn) {
          // eslint-disable-next-line no-console
          console.warn('[home] onSettings threw:', err);
        }
      }
    }
  }

  settingsBtn.addEventListener('click', () => {
    // Tap-to-close while panel is open: bypass gate entirely.  Spec §2
    // "tapping the Settings button again while open" dismissal.
    if (isSettingsOpen()) {
      resetGate();
      try { onSettings(); }
      catch (err) {
        if (typeof console !== 'undefined' && console.warn) {
          // eslint-disable-next-line no-console
          console.warn('[home] onSettings threw:', err);
        }
      }
      return;
    }
    advanceGate();
  });

  // Outside-tap reset: any tap that's NOT on the Settings button while
  // the gate is partially advanced snaps it back to neutral.  Listener
  // is on the document so taps anywhere -- including on Learn/Quiz --
  // are caught.  Using click (not pointerdown) so jsdom's .click()
  // fires it and iOS Safari's click-after-touch is the universal
  // "user tapped something" signal.
  function handleDocumentClick(ev) {
    if (gateState === 0) return;
    if (isSettingsOpen()) return;   // gate is reset when the panel opens
    const target = ev.target;
    if (target instanceof Node && settingsBtn.contains(target)) return;
    resetGate();
  }
  if (typeof document !== 'undefined') {
    document.addEventListener('click', handleDocumentClick, true);
  }

  // --- Return handles ------------------------------------------------------
  return {
    element: el,
    hide() {
      el.style.display = 'none';
      resetGate();
    },
    show() {
      el.style.display = '';
      resetGate();
      resetSelectState();
    },
    setSelectAnimationMs(ms) {
      if (!Number.isFinite(ms) || ms < 0) return;
      buttons.style.setProperty('--home-select-anim-ms', `${ms}ms`);
    },
    /**
     * Push a new mascot config (from loadAppConfig()) into the mascot.
     * Numeric tunables apply to subsequent timer scheduling; style
     * tunables apply synchronously via CSS custom properties.
     */
    setMascotConfig(config) {
      mascot.setConfig(config);
    },
    /**
     * Drop DOM listeners + timers.  Mirrors createSettingsPanel's
     * destroy API; useful from initApp.destroy().
     */
    destroy() {
      resetGate();
      mascot.destroy();
      if (typeof document !== 'undefined') {
        document.removeEventListener('click', handleDocumentClick, true);
      }
      if (el.parentNode) el.parentNode.removeChild(el);
    },
    // Test-only hooks.
    __getGateState() { return gateState; },
    __resetGate() { resetGate(); },
  };
}
